import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Loader } from "../../Components/Loader/loader";
import SearchComponent from "../../Components/SearchComponent/searchComponent";
import Footer from "../Footer/footer";
import ListItem from "../../Components/ListItem/listItem";

import styles from "./mainPage.module.scss";
import {
    getArticle2,
} from "../../assets/api/apiCall";
import scrollTo from "../../Components/Helpers/scrollTo";
import MainSplide from "../../Components/Splide/main/mainSplide";
import translation from "../../Components/Helpers/translation";
import { Helmet } from "react-helmet";
import DropdownMonths from "../../Components/DropdownMonth/DropdownMonth";

function MainPage({ categories, openSortRef }) {
    const [dataSlider, setDataSlider] = useState(null);
    const [data, setData] = useState(null);
    const [dataItems, setDataItems] = useState(null);
    const [monthsFromApi, setMonthsFromApi] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // const [categories, setCategories] = useState(null);

    const [countBefore, setCountBefore] = useState(null);
    const [isSearch, setIsSearch] = useState(false);

    const [closed, setClosed] = useState(true);

    const loaderRef = useRef(null);
    const loaderRefBig = useRef(null);
    const shadowRef = useRef(null);
    const midleRef = useRef(null);
    // const openSortRef = useRef(null)
    const { lang } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);

    const normalizePostsResponse = (json) => {
        // Backend can return:
        // - { items: [...] }
        // - { items: { "0": {...}, "1": {...} } }
        // - { "0": {...}, "1": {...}, metaData: {...}, months: [...] }
        if (!json || typeof json !== "object") return { items: [], months: [] };

        const rawItems = json.items ?? json;
        let items = [];

        if (Array.isArray(rawItems)) {
            items = rawItems;
        } else if (rawItems && typeof rawItems === "object") {
            const numericKeys = Object.keys(rawItems).filter((k) => /^\d+$/.test(k));
            if (numericKeys.length > 0) {
                items = numericKeys
                    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                    .map((k) => rawItems[k]);
            } else if (rawItems !== json) {
                items = Object.values(rawItems);
            } else {
                items = [];
            }
        }

        const months = Array.isArray(json.months) ? json.months : [];
        return { items, months };
    };

    const getMonthsFallback = (items) => {
        // created_at: "YYYY-MM-DD" -> "MM.YYYY"
        if (!Array.isArray(items)) return [];
        const monthsSet = new Set();
        items.forEach((item) => {
            const createdAt = item?.created_at;
            if (typeof createdAt === "string" && createdAt.length >= 7) {
                const year = createdAt.substring(0, 4);
                const month = createdAt.substring(5, 7);
                if (year && month) monthsSet.add(`${month}.${year}`);
            }
        });
        return Array.from(monthsSet);
    };

    const months = monthsFromApi?.length ? monthsFromApi : getMonthsFallback(dataItems);
    const filteredArticles = dataItems;

    const normalizeDateForApi = useCallback((date) => {
        // Accepts: "MM.YYYY", "M.YYYY", "MM-YYYY", "M-YYYY"
        // Returns: "MM-YYYY" (e.g. "4.2026" -> "04-2026")
        if (!date) return null;
        const normalized = String(date).trim().replace(".", "-");
        const match = normalized.match(/^(\d{1,2})-(\d{4})$/);
        if (!match) return null;
        const monthNum = parseInt(match[1], 10);
        const yearNum = parseInt(match[2], 10);
        if (!Number.isInteger(monthNum) || !Number.isInteger(yearNum)) return null;
        if (monthNum < 1 || monthNum > 12) return null;
        return `${String(monthNum).padStart(2, "0")}-${yearNum}`;
    }, []);

    useEffect(() => {
        if (monthsFromApi?.length) {
            // months from backend response: json.months
            // eslint-disable-next-line no-console
            console.log("[MainPage] months from API (json.months):", monthsFromApi);
        }
    }, [monthsFromApi]);

    useEffect(() => {
        if (!monthsFromApi?.length && Array.isArray(dataItems)) {
            const fallbackMonths = getMonthsFallback(dataItems);
            // months derived client-side from created_at
            // eslint-disable-next-line no-console
            console.log("[MainPage] months fallback from posts created_at:", fallbackMonths);
        }
    }, [monthsFromApi, dataItems]);

    const getPostsQueryFromLocation = useCallback((pageOverride = null) => {
        const urlParams = new URLSearchParams(window.location.search);
        // Keep stable defaults for list endpoint.
        urlParams.set("pagination", "true");
        const pageFromUrl = parseInt(urlParams.get("page") || "1", 10);
        const page = Number.isInteger(pageOverride) ? pageOverride : (Number.isInteger(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1);
        urlParams.set("page", String(page));

        // Normalize date to backend format: "MM-YYYY"
        const date = urlParams.get("date");
        const apiDate = normalizeDateForApi(date);
        if (apiDate) urlParams.set("date", apiDate);
        return urlParams;
    }, [normalizeDateForApi]);

    const getUiQueryFromLocation = () => {
        // Only user-meaningful filters should be visible in the browser URL.
        const urlParams = new URLSearchParams(window.location.search);
        const allowed = ["date", "search", "categories", "limit", "page"];
        const uiParams = new URLSearchParams();
        allowed.forEach((k) => {
            const v = urlParams.get(k);
            if (!v) return;
            if (k === "page") {
                const p = parseInt(v, 10);
                if (Number.isInteger(p) && p > 1) uiParams.set("page", String(p));
                return;
            }
            uiParams.set(k, v);
        });
        return uiParams;
    };

    const pushUiUrl = (patch = {}) => {
        const queryString = window.location.origin + "/" + lang;
        const uiParams = getUiQueryFromLocation();
        Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined || v === "" || v === false) {
                uiParams.delete(k);
                return;
            }
            if (k === "page") {
                const p = parseInt(String(v), 10);
                if (!Number.isInteger(p) || p <= 1) uiParams.delete("page");
                else uiParams.set("page", String(p));
                return;
            }
            uiParams.set(k, String(v));
        });
        const qs = uiParams.toString();
        window.history.pushState({}, null, qs ? `${queryString}?${qs}` : queryString);
    };

    const loadPosts = useCallback(async ({ page = null, cumulative = false } = {}) => {
        const requestedPage =
            page ?? (parseInt(new URLSearchParams(window.location.search).get("page") || "1", 10) || 1);
        const safePage = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

        if (!cumulative || safePage <= 1) {
            const urlParams = getPostsQueryFromLocation(safePage);
            const query = `?lang=${lang}&${urlParams.toString()}`;
            const json = await getArticle2(query);
            const { items, months } = normalizePostsResponse(json);
            if (months?.length) setMonthsFromApi(months);
            setData(json);
            setDataItems(items);
            setCountBefore(items.length);
            setCurrentPage(json?.metaData?.currentPage || safePage);
            return { json, items, months };
        }

        // If user lands on page>1, fetch pages 1..N and concatenate
        let combinedItems = [];
        let lastJson = null;
        let lastMonths = [];
        for (let p = 1; p <= safePage; p++) {
            const urlParams = getPostsQueryFromLocation(p);
            const query = `?lang=${lang}&${urlParams.toString()}`;
            // eslint-disable-next-line no-await-in-loop
            const json = await getArticle2(query);
            const { items, months } = normalizePostsResponse(json);
            combinedItems = combinedItems.concat(items);
            lastJson = json;
            if (months?.length) lastMonths = months;
        }

        if (lastMonths?.length) setMonthsFromApi(lastMonths);
        setData(lastJson);
        setDataItems(combinedItems);
        setCountBefore(combinedItems.length);
        setCurrentPage(safePage);
        return { json: lastJson, items: combinedItems, months: lastMonths };
    }, [getPostsQueryFromLocation, lang]);

    const handleLoadMore = () => {
        const nextPageFromMeta = data?.metaData?.nextPage;
        const lastPage = data?.metaData?.lastPage;
        const current = data?.metaData?.currentPage || currentPage || 1;
        const nextPage = nextPageFromMeta || current + 1;

        // Backend is the source of truth: if there's no nextPage, stop.
        if (!nextPageFromMeta || (lastPage && current >= lastPage)) return;
        if (isLoadingMore) return;

        setIsLoadingMore(true);
        shadowRef.current.classList.add(styles.itemsWrapperShadowShow);
        loaderRef.current.classList.add(styles.loaderShow);

        const urlParams = new URLSearchParams(window.location.search);
        let readCategories = urlParams.get("categories");
        let readSearch = urlParams.get("search");
        let readDate = urlParams.get("date");

        let querySearch = `?lang=${lang}`;
        if (readSearch) {
            querySearch += `&search=${readSearch}`;
        }
        if (readCategories) {
            querySearch += `&categories=${readCategories}`;
        }
        if (readDate) {
            const apiDate = normalizeDateForApi(readDate);
            querySearch += `&date=${apiDate || readDate}`;
        }

        // Update URL with page only if > 1.
        pushUiUrl({ page: nextPage });

        querySearch += `&page=${nextPage}&pagination=true`;

        setTimeout(() => {
            setCountBefore(dataItems.length);
            getArticle2(querySearch).then((json) => {
                const { items, months } = normalizePostsResponse(json);
                if (months?.length) setMonthsFromApi(months);
                setData(json);
                setCurrentPage(json?.metaData?.currentPage || nextPage);

                let combined = dataItems.concat(items);
                // console.log("array ", combined);
                setDataItems(combined);
                // json.items.forEach(element => {
                //     setDataItems([...dataItems, element])
                // });

                setTimeout(() => {
                    shadowRef.current.classList.remove(
                        styles.itemsWrapperShadowShow
                    );
                    loaderRef.current.classList.remove(styles.loaderShow);

                    const dates = document.querySelectorAll(
                        '[id^="searchElement"]'
                    );
                    const newFirstElement = document.getElementById(
                        `searchElement-${dates.length - 1}`
                    );
                    scrollTo(
                        newFirstElement.offsetTop +
                        newFirstElement.offsetHeight,
                        300
                    );
                }, 400);
            }).finally(() => {
                setIsLoadingMore(false);
            });
        }, 100);
    };

    function categoryButtonClick(index, value, close = false) {
        if (close) setClosed(!closed);
        shadowRef.current?.classList.add(styles.itemsWrapperShadowShow);
        loaderRefBig.current?.classList.add(styles.loaderShow);

        if (index === "all") {
            setTimeout(() => {
                setIsSearch(false);
            }, 300);
            if (openSortRef)
                if (openSortRef.current)
                    // openSortRef.current.textContent = size.width > 800 ? translation.site.main.categories.showList : translation.site.main.categories.showListShort
                    openSortRef.current.textContent =
                        translation.site.main.categories.showList;
        } else {
            setTimeout(() => {
                setIsSearch(true);
            }, 300);
            if (openSortRef)
                if (openSortRef.current)
                    openSortRef.current.textContent = value;
        }

        const urlParams = new URLSearchParams(window.location.search);
        let readSearch = urlParams.get("search");
        let readDate = urlParams.get("date");
        let querySearch = `?lang=${lang}`;
        // Keep browser URL clean; page is internal only.
        const uiParams = getUiQueryFromLocation();

        if (readSearch) {
            querySearch += `&search=${readSearch}&categories=${index}`;
            uiParams.set("search", readSearch);
        } else {
            querySearch += `&categories=${index}`;
            uiParams.delete("search");
        }
        uiParams.set("categories", index);
        if (readDate) {
            const apiDate = normalizeDateForApi(readDate);
            querySearch += `&date=${apiDate || readDate}`;
            uiParams.set("date", readDate);
        }

        // Reset paging on filter change.
        uiParams.delete("page");
        window.history.pushState({}, null, `${window.location.origin}/${lang}${uiParams.toString() ? `?${uiParams.toString()}` : ""}`);

        querySearch += `&page=1&pagination=true`;
        setCurrentPage(1);

        //or reload
        // window.location.search = search;

        getArticle2(querySearch).then((json) => {
            const { items, months } = normalizePostsResponse(json);
            if (months?.length) setMonthsFromApi(months);

            if (items.length === 0) {
                for (let i = 0; i < dataItems.length; i++) {
                    const obj = document.getElementById(`searchElement-${i}`);
                    obj.style.height = obj.scrollHeight + "px";
                    setTimeout(() => {
                        obj.style.height = 0;
                    }, 100);
                }
            }
            // const obj = document.getElementById(`searchElement-${dataItems.length - 1}`)
            if (dataItems.length > items.length) {
                const index = dataItems.length - items.length;

                for (
                    let i = dataItems.length - index;
                    i < dataItems.length;
                    i++
                ) {
                    const obj2 = document.getElementById(`searchElement-${i}`);
                    obj2.style.height = obj2.scrollHeight + "px";
                    setTimeout(() => {
                        obj2.style.height = 0;
                    }, 100);
                }
            }

            setCountBefore(dataItems.length);
            setTimeout(() => {
                setData(json);
                setDataItems(items);
                shadowRef.current.classList.remove(
                    styles.itemsWrapperShadowShow
                );
                loaderRef.current.classList.remove(styles.loaderShow);
            }, 400);

            // setTimeout(() => {
            //     setData(json)
            //     setDataItems(json.items)
            // }, 400)
            // setTimeout(() => {
            //     shadowRef.current.classList.remove(styles.itemsWrapperShadowShow)
            //     loaderRef.current.classList.remove(styles.loaderShow)
            // }, 500);
        });
    }

    function tagClick(readName, readId) {
        const el = document.getElementById("openSort");
        if (el) {
            el.innerText = readName;
            const topEl = document.getElementById("mainListTop");
            scrollTo(topEl.clientHeight, 500);

            categoryButtonClick(readId, readName);
        }
    }



    function handleMonthClick(month) {
        const urlParams = new URLSearchParams(window.location.search);

        if (!month) {
            sessionStorage.removeItem("selectedMonth");
            urlParams.delete("date");
        } else {
            sessionStorage.setItem("selectedMonth", month);
            // Keep UI date as backend-provided value (typically "MM.YYYY"),
            // request normalization is handled in normalizeDateForApi().
            urlParams.set("date", String(month));
        }

        // Reset paging on date change (URL should not show page=1).
        pushUiUrl({ date: month || null, page: 1 });

        setIsLoading(true);
        setCurrentPage(1);
        loadPosts({ page: 1, cumulative: false }).finally(() => {
            setTimeout(() => {
                setSelectedMonth(month);
                setIsLoading(false);
            }, 250);
        });
    }
    



    useEffect(() => {
        if (!dataSlider) {
            getArticle2(`?lang=${lang}&slider=true`).then((json) => {
                setDataSlider(json);
            });
        }
    }, [lang, dataSlider]);
    

    
    useEffect(() => {
        const pageFromUrl = parseInt(
            new URLSearchParams(window.location.search).get("page") || "1",
            10
        );
        const pageRaw = Number.isInteger(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
        // If user lands on a deep page via URL, reset to page=1 (too slow to prefetch).
        const page = pageRaw > 1 ? 1 : pageRaw;

        if (pageRaw > 1) {
            // Remove page from URL (page=1 is implicit)
            const queryString = window.location.origin + "/" + lang;
            const uiParams = getUiQueryFromLocation();
            uiParams.delete("page");
            const qs = uiParams.toString();
            window.history.replaceState({}, null, qs ? `${queryString}?${qs}` : queryString);
        }

        // If someone pasted date as "MM-YYYY", keep it (UI accepts both).
        const urlParams = new URLSearchParams(window.location.search);
        const readDate = urlParams.get("date");
        void readDate;

        const uiParamsForCache = getUiQueryFromLocation();
        uiParamsForCache.delete("page");
        const cacheKey = `articlesData:${lang}:${uiParamsForCache.toString()}`;
        const savedData = sessionStorage.getItem(cacheKey);

        if (savedData) {
            const parsedData = JSON.parse(savedData);
            const { items, months } = normalizePostsResponse(parsedData);
            if (months?.length) setMonthsFromApi(months);
            setData(parsedData);
            setDataItems(items);
            setCountBefore(items.length);
        }

        // Always hit the posts endpoint on page entry to refresh months/items.
        // Cache is only for faster initial paint, not a source of truth.
        loadPosts({ page, cumulative: false }).then(({ json }) => {
            sessionStorage.setItem(cacheKey, JSON.stringify(json));
        });
    }, [lang, loadPosts]);

    
    useEffect(() => {
        const savedScrollPosition = sessionStorage.getItem("scrollPosition");
        const fromArticle = sessionStorage.getItem("fromArticle");
    
        if (savedScrollPosition && fromArticle === "true") {
            setTimeout(() => {
                window.scrollTo(0, parseInt(savedScrollPosition, 10));
            }, 50);
        }
    
        sessionStorage.removeItem("fromArticle");
    }, [dataItems]);
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const readDate = urlParams.get("date");

        // URL is the source of truth for selected month.
        // If there's no date in URL (e.g. /pl), clear any persisted selection.
        if (readDate) {
            setSelectedMonth(readDate);
            sessionStorage.setItem("selectedMonth", readDate);
        } else {
            setSelectedMonth(null);
            sessionStorage.removeItem("selectedMonth");
        }
    }, []);
    
    
    
    return (
        <>
            <Helmet>
                <title>{`Nifc Blog - ${lang === "en" ? "Main Page" : "Strona główna"
                    }`}</title>
                <meta
                    name="description"
                    content={
                        lang === "en"
                            ? "The Nifc blog is the largest collection of all content related to the Romantic era"
                            : "Blog Nifc to największy zbiór wszystkich treści połączonych z epoką romantyzmu"
                    }
                />
            </Helmet>
            <Loader data={data || dataSlider} scroll={false} />
            <div className={styles.mainPageWrapper}>
                <div id="mainListTop" className={styles.top}>
                    <MainSplide data={dataSlider} tagClick={tagClick} />
                </div>
                <div className={styles.middle} ref={midleRef}>
                    <SearchComponent
                        openSortRef={openSortRef}
                        setClosed={setClosed}
                        closed={closed}
                        categoryButtonClick={categoryButtonClick}
                        setData={setData}
                        dataItems={dataItems}
                        setDataItems={setDataItems}
                        setCountBefore={setCountBefore}
                        countBefore={countBefore}
                        categories={categories}
                        setIsSearch={setIsSearch}
                        shadowRef={shadowRef}
                        loaderRef={loaderRefBig}
                        midleRef={midleRef}
                    />

                    <div className={styles.dropdownMonths}>
                        <DropdownMonths
                            months={months}
                            selectedMonth={selectedMonth}
                            handleMonthClick={handleMonthClick}
                        />
                    </div>

                    <div
                        className={`${styles.itemsWrapper} ${styles.searched}`}
                    >
                        <div
                            className={`${styles.search} ${isSearch ? styles.active : ""
                                }`}
                        >
                            <span className={`${styles.searchText} `}>
                                {translation.site.main.searchResults}
                            </span>
                            <span className={styles.counter}>
                                {data
                                    ? data.metaData
                                        ? data.metaData.totalItems
                                        : "0"
                                    : "0"}
                            </span>
                        </div>
                        <div className={styles.articlesWrapper}>
                            {isLoading ? (
                                <div className={styles.loaderContainer}>
                                    <Loader />
                                </div>
                            ) : filteredArticles && filteredArticles.length > 0 ? (
                                filteredArticles.map((item, index) => (
                                    <ListItem
                                        id={`searchElement-${index}`}
                                        item={item}
                                        key={index}
                                        animation={index >= countBefore}
                                        categoryButtonClick={categoryButtonClick}
                                        tagClick={tagClick}
                                    />
                                ))
                            ) : (
                                <p>Brak artykułów w tym miesiącu</p>
                            )}
                        </div>

                        <div
                            className={styles.itemsWrapperShadow}
                            ref={shadowRef}
                        >
                            <div
                                className={`${styles.loader} ${styles.loaderBig}`}
                                ref={loaderRefBig}
                            />
                        </div>
                    </div>
                    <div className={styles.loader} ref={loaderRef} />
                    {data ? (
                        data.metaData ? (
                            dataItems ? (
                                // Backend is the source of truth for pagination.
                                // If nextPage is missing/null, there is nothing more to load.
                                data.metaData.nextPage &&
                                    data.metaData.currentPage < data.metaData.lastPage ? (
                                    <button
                                        type={"button"}
                                        className={styles.loadMore}
                                        onClick={handleLoadMore}
                                        disabled={isLoadingMore}
                                    >
                                        {translation.site.btns.loadMore}
                                    </button>
                                ) : null
                            ) : null
                        ) : null
                    ) : null}
                </div>
                <Footer />
            </div>
        </>
    );
}

export default MainPage;
